import { useRef } from "react";
import {
    BOT_TRAP_RENDERED_AT_FIELD,
    BotTrapFieldName,
    BotTrapFields,
} from "@common/types/botTrapTypes.ts";

type UseBotTrap = {
    /** render this inside the form - it is invisible and unreachable for real users */
    botTrapField: JSX.Element;
    /** spread into the request body on submit */
    getBotTrapFields: () => BotTrapFields;
};

/**
 * Hidden-field + submit-timer bot trap for public forms.
 *
 * The field is pushed off screen with CSS instead of type="hidden" (spam scripts know to skip
 * hidden inputs), and aria-hidden/tabIndex keep screen reader and keyboard users away from it,
 * so anything that arrives filled in came from a script.
 *
 * The input is deliberately uncontrolled and read from the DOM on submit: a script that assigns
 * `input.value` directly never fires React's change event, so a controlled input would report the
 * trap as empty and miss the laziest kind of filler.
 *
 * @param field which trap field this form uses - see BOT_TRAP_FIELDS
 */
export function useBotTrap(field: BotTrapFieldName): UseBotTrap {
    const trapRef = useRef<HTMLInputElement>(null);
    const renderedAt = useRef<number>(Date.now());

    const botTrapField = (
        <input
            ref={trapRef}
            type="text"
            name={field}
            defaultValue=""
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
        />
    );

    const getBotTrapFields = (): BotTrapFields => {
        const fields: BotTrapFields = { [BOT_TRAP_RENDERED_AT_FIELD]: renderedAt.current };
        fields[field] = trapRef.current?.value ?? '';
        return fields;
    };

    return { botTrapField, getBotTrapFields };
}
